import history from '@history';
import { authApi } from 'app/services/api';
import {
  httpAccountsApi,
  httpAiApi,
  httpApplyApi,
  httpAusApi,
  httpCrmApi,
  httpDocumentsApi,
  httpFeesApi,
  httpFormsApi,
  httpIntegrationsApi,
  httpLoanProcessingApi,
  httpLoansApi,
  httpNotificationsApi,
  httpPricingApi,
  httpSkynetApi,
  httpTelephonyApi,
} from 'app/services/http';
import { shutdownIntercomMessenger } from 'app/services/intercom';
import dayjs from 'dayjs';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';

import { ACCOUNTS_API_REFRESH_TOKEN, UNPROTECTED_ROUTES } from 'constants/auth';
import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import {
  IMPERSONATED_FROM_COMPANY_ID,
  IMPERSONATED_FROM_PATH_NAME,
  IS_REFRESHING_IN_OTHER_TAB,
  LS_ACCOUNTS_CLIENT_ID,
  LS_ACCOUNTS_COMPANY_ID,
  LS_KEYS_TO_RESET_ON_LOGOUT,
  LS_KEYS_TO_RESET_ON_LOGOUT_EXPLICIT,
} from 'constants/localStorage';
import { masterSettings } from 'constants/master';
import { DEFAULT_THEME_MODE, IS_SWITCH_THEME_MODE_ALLOWED, themeOptions } from 'constants/themes';
import { aiAggregator } from 'utils/aiAggregator';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { getCompanyId, getDomainName } from 'utils/domain';
import { generateUnauthorizedAxiosError } from 'utils/error';
import { EventEmitter } from 'utils/eventEmitter';
import {
  checkWillReloadOnForbidden,
  isLoanEditorRoute,
  isReadRequestError,
} from 'utils/forbiddenReload';
import { logger } from 'utils/logger';
import { getIsNativePlatform } from 'utils/platform';
import { delay } from 'utils/promise';

import { httpUnderwritingApi } from '../http';

import {
  DEBOUNCE_DELAY_MS,
  EXPIRED_TOKEN_CHALLENGE_MARKER,
  MAX_REFRESH_TIMEOUT_MS,
  PROACTIVE_REFRESH_LEAD_TIME_SECONDS,
  REFRESH_FATAL_STATUSES,
  REFRESH_IN_OTHER_TAB_MAX_ATTEMPTS,
  REFRESH_RETRY_MAX_ATTEMPTS,
  REFRESH_SCHEDULE_JITTER_MS,
  RETRY_DELAY_MS,
} from './accountsJwtService.constants';

const REFRESH_TOKEN_EXPIRED_MESSAGE = 'RefreshToken is expired';
const REFRESH_ABANDONED_MESSAGE = 'Refresh abandoned: the session ended while it was in flight';

// DEV-6375 labels a 401 whose inbound token was already past `exp`. Everything else keeps the bare 401,
// so the absence of the header says nothing — only its presence is informative.
const hasExpiredTokenChallenge = error => {
  const challenge = error?.response?.headers?.['www-authenticate'];

  return _.isString(challenge) && challenge.includes(EXPIRED_TOKEN_CHALLENGE_MARKER);
};

// Matches the loan pipelines page (/pipelines and /pipelines/:id). The Kanban view's
// backing GetKanbanViewData endpoint is [Authorize]-only — never opened to sub-company
// TPO brokers — so a broker whose pipeline resolves to Kanban 403s on that GET the
// instant the page mounts. That is the same degradable-read shape the loan-editor route
// suppression below already exists for; without it the global reload loops every ~5s,
// wiping in-progress UI state such as the Add Loan wizard. Defense-in-depth alongside the
// LoanPipelines.js fix that stops dispatching the kanban fetch outside the Kanban view
// (DEV-5006).
const PIPELINES_ROUTE_PATTERN = /^\/pipelines(\/|$)/;

const isPipelinesRoute = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return PIPELINES_ROUTE_PATTERN.test(window.location.pathname);
};

class AccountsJwtService extends EventEmitter {
  // Shared promise for any in-flight refresh. All concurrent callers (request interceptors,
  // response interceptors, and handleAuthentication) attach to this same promise so only one
  // POST /refresh is ever in flight at a time (DEV-4332 single-flight fix).
  #refreshPromise = null;
  #isLoggedOut = false;
  #storage;
  #jwtManager;

  // Mirrors the Redux `selectIsSubCompanyUser` selector (same underlying `subCompany` field
  // from the authenticated-session payload), cached locally instead of read from the store:
  // this service sits below the store in the module graph (store/global -> reducers ->
  // store/auth/actions -> app/services/jwtService), so importing the store here would create a
  // circular import. Set from every signInWithToken() resolution — the single funnel every
  // login/auto-login/re-login path already goes through — so it is current by the time any
  // protected request can fire (DEV-4226).
  #isSubCompanyUser = false;

  // Whether a session was ever actually established in this run — the difference between a session
  // that DIED (worth telling the user about) and a user who simply is not signed in (not an error).
  #hasAuthenticatedThisSession = false;

  // Handle of the pending proactive-refresh timer, and the single wake handler bound to
  // visibilitychange/focus/online. Both are nulled rather than left dangling so logout can stop the
  // cycle, and so a re-arm through #markAuthenticated cannot bind a second listener.
  #refreshTimerId = null;
  #wakeHandler = null;
  #refreshRetryCount = 0;

  // Bumped whenever the session identity ends or changes. A refresh in flight across that boundary belongs
  // to the previous session and must not persist its result — see refreshToken.
  #sessionEpoch = 0;

  constructor(storage, jwtManager) {
    super();
    this.#storage = storage;
    this.#jwtManager = jwtManager;
  }

  // "RefreshToken is expired" is only meaningful to someone whose session died while they were
  // USING the app. Told to a user who never signed in, it is noise they can neither act on nor
  // understand — and on iOS it is near-permanent, because keychain items outlive app deletion, so
  // one stale token makes it fire on every cold launch of a reinstalled app. The logout still
  // happens; only the message is withheld (a null message is already a no-op for the listener).
  #emitAutoLogoutOnce = (message, error) => {
    if (this.#isLoggedOut) {
      return;
    }

    this.#isLoggedOut = true;
    this.emit('onAutoLogout', this.#hasAuthenticatedThisSession ? message : null, error);
  };

  // The single funnel every login / auto-login / re-login path already goes through, which makes it the
  // one place the refresh cycle can be armed without a fourth call site drifting out of sync.
  #markAuthenticated = () => {
    this.#hasAuthenticatedThisSession = true;
    this.#startRefreshCycle();
  };

  // DEV-6376: renew ahead of `exp` rather than waiting for a request to discover the token is dead —
  // a demand-driven renewal makes a burst of failures the trigger.
  #startRefreshCycle = () => {
    // Arming is a fresh start — reLogin re-arms without going through logout, so a previous identity's
    // exhausted count would otherwise deny the new session its retries.
    this.#refreshRetryCount = 0;
    this.#attachWakeListeners();
    this.#scheduleRefreshAhead();
  };

  #stopRefreshCycle = () => {
    if (_.isNil(this.#refreshTimerId)) {
      return;
    }

    clearTimeout(this.#refreshTimerId);
    this.#refreshTimerId = null;
  };

  // Logout is the one point where the session is gone for good rather than merely being re-scheduled,
  // so the wake listeners come down with the timer. The next sign-in re-arms both.
  #teardownRefreshCycle = () => {
    this.#stopRefreshCycle();
    this.#detachWakeListeners();
    this.#refreshRetryCount = 0;
  };

  #scheduleRefreshAhead = () => {
    this.#stopRefreshCycle();

    if (this.#isLoggedOut) {
      return;
    }

    const accessToken = this.#jwtManager.getAccessToken();

    if (!accessToken?.expirationDate) {
      return;
    }

    const millisecondsAhead = dayjs(accessToken.expirationDate)
      .subtract(PROACTIVE_REFRESH_LEAD_TIME_SECONDS, 'seconds')
      .diff(dayjs());

    // A token already inside the lead window fires on the next tick rather than being skipped, and a
    // token whose lifetime overflows setTimeout is woken in chunks and re-scheduled from there. The
    // jitter de-synchronises tabs: they all read the same `exp` from shared storage, so identical wake
    // times would have them all read IS_REFRESHING_IN_OTHER_TAB as false before any of them writes it.
    const delayMs = Math.min(
      Math.max(millisecondsAhead, 0) + Math.random() * REFRESH_SCHEDULE_JITTER_MS,
      MAX_REFRESH_TIMEOUT_MS,
    );

    this.#refreshTimerId = setTimeout(this.#runRefreshCycle, delayMs);
  };

  // Shared by the timer and the wake listeners. A throttled background tab can miss its tick entirely,
  // so waking must run the same cycle rather than trust the timer to have fired.
  #runRefreshCycle = async () => {
    // Cleared, not just nulled: the wake handler calls this directly while a timer is still pending,
    // and nulling the handle would orphan that setTimeout instead of cancelling it.
    this.#stopRefreshCycle();

    if (this.#isLoggedOut) {
      return;
    }

    const accessToken = this.#jwtManager.getAccessToken();
    const refreshToken = this.#jwtManager.getRefreshToken();

    if (!this.#jwtManager.getIsTokenValid(refreshToken)) {
      return;
    }

    // Re-arming on a still-valid token is what makes the wake path cheap: N tabs waking on a healthy
    // session issue zero refreshes between them, not N.
    if (this.#jwtManager.getIsTokenValid(accessToken)) {
      this.#refreshRetryCount = 0;
      this.#scheduleRefreshAhead();

      return;
    }

    try {
      await this.#doRefresh();
    } catch (error) {
      // #doRefresh has already ended the session for an unrecoverable failure, so retrying would only
      // arm a timer against tokens that no longer exist.
      if (!this.#isSessionUnrecoverable(error)) {
        this.#scheduleRefreshRetry();
      }

      return;
    }

    this.#refreshRetryCount = 0;
    this.#scheduleRefreshAhead();
  };

  // A transient failure keeps the session alive (see #isSessionUnrecoverable), so without this the tab
  // would be left with no schedule at all for the rest of the session and silently fall back to the
  // 401-burst behaviour. #scheduleRefreshAhead is not usable here: the token is already stale, so it
  // clamps to ~now and would retry every few seconds for as long as the network is down. Back off
  // instead, then hand back to the wake listeners once the attempts are spent.
  #scheduleRefreshRetry = () => {
    if (this.#isLoggedOut || this.#refreshRetryCount >= REFRESH_RETRY_MAX_ATTEMPTS) {
      return;
    }

    const delayMs = RETRY_DELAY_MS * 2 ** this.#refreshRetryCount;

    this.#refreshRetryCount += 1;
    this.#refreshTimerId = setTimeout(this.#runRefreshCycle, delayMs);
  };

  // Bound once, and bound EARLY (from init, before any hub exists) so this runs ahead of
  // SignalRConnection's own visibilitychange listener — a hub that reconnects first would negotiate on
  // the stale token and be dropped again.
  #attachWakeListeners = () => {
    if (this.#wakeHandler) {
      return;
    }

    this.#wakeHandler = () => this.#runRefreshCycle();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.#wakeHandler);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.#wakeHandler);
      window.addEventListener('online', this.#wakeHandler);
    }
  };

  #detachWakeListeners = () => {
    if (!this.#wakeHandler) {
      return;
    }

    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#wakeHandler);
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', this.#wakeHandler);
      window.removeEventListener('online', this.#wakeHandler);
    }

    this.#wakeHandler = null;
  };

  async init() {
    this.#attachWakeListeners();

    const accessToken = this.#jwtManager.getAccessToken();
    const refreshToken = this.#jwtManager.getRefreshToken();

    const isAccessTokenValid = this.#jwtManager.getIsTokenValid(accessToken);
    const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);

    const isCurrentCompanyIdNotExist = !getCompanyId();

    const isInvalidTokens = !isAccessTokenValid && !isRefreshTokenValid;

    if (isInvalidTokens || isCurrentCompanyIdNotExist) {
      await this.getInitialCompanyId();
    }

    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    const httpApisToIntercept = [
      httpSkynetApi,
      httpPricingApi,
      httpNotificationsApi,
      httpFormsApi,
      httpDocumentsApi,
      httpLoansApi,
      httpLoanProcessingApi,
      httpAusApi,
      httpUnderwritingApi,
      httpAiApi,
      httpApplyApi,
      httpCrmApi,
      httpIntegrationsApi,
      httpTelephonyApi,
    ];

    httpApisToIntercept.forEach(httpApi => this.#injectRequestInterceptorOfAuthToken(httpApi));
    httpApisToIntercept.forEach(httpApi => this.#injectResponseInterceptorOfAuthToken(httpApi));

    const httpApisToInterceptOfForbiddenStatus = [
      httpPricingApi,
      httpAccountsApi,
      httpFeesApi,
      httpFormsApi,
      httpDocumentsApi,
      httpNotificationsApi,
      httpSkynetApi,
      httpTelephonyApi,
      httpUnderwritingApi,
      httpApplyApi,
      httpCrmApi,
      httpIntegrationsApi,
      // Every remaining client the loan editor can call (manual-actions, loans, AUS, AI chat) —
      // previously absent here, so a 403 on those never even reached this interceptor and had to
      // be allowlisted per-endpoint instead (DEV-4226).
      httpLoansApi,
      httpLoanProcessingApi,
      httpAusApi,
      httpAiApi,
    ];

    httpApisToInterceptOfForbiddenStatus.forEach(httpApi =>
      this.#injectResponseInterceptorOfForbiddenStatus(httpApi),
    );

    httpAccountsApi.injectRequestInterceptor(async config => {
      // Sign-in and other pre-auth calls must never be gated on a refresh: a stored refresh token the
      // server already rejected would fail the refresh, and that 400 would surface on the login form as
      // "email or password incorrect" without the sign-in request ever being sent.
      if (UNPROTECTED_ROUTES.includes(config.url)) {
        return config;
      }

      const accessToken = this.#jwtManager.getAccessToken();
      const refreshToken = this.#jwtManager.getRefreshToken();

      const isAccessTokenValid = this.#jwtManager.getIsTokenValid(accessToken);
      const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);

      const isRefreshRequest = config.url === ACCOUNTS_API_REFRESH_TOKEN;
      const isRefreshNeeded = !isRefreshRequest && !isAccessTokenValid && isRefreshTokenValid;

      if (isAccessTokenValid) {
        config.headers.Authorization = `Bearer ${accessToken?.value}`;
      }

      if (isRefreshNeeded) {
        return this.handleRefresh(config);
      }

      return config;
    });

    httpAccountsApi.injectResponseInterceptor(
      response => response,
      async error => {
        // A request-stage rejection reaches here too (axios chains request interceptors, dispatch and
        // response interceptors into one promise), and an abandoned refresh rejects with a plain Error that
        // carries no config — so every read of it is optional or this throws a TypeError instead.
        const isRefreshRequest = error.config?.url === ACCOUNTS_API_REFRESH_TOKEN;

        const inappropriateResponseStatuses = [
          HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED,
          ...getConditionalArray(isRefreshRequest, [
            HTTP_RESPONSE_STATUS_CODES.BAD_REQUEST,
            HTTP_RESPONSE_STATUS_CODES.NOT_FOUND,
          ]),
        ];

        const isInterceptorSkipRequired = UNPROTECTED_ROUTES.includes(error.config?.url);
        const isAutoLogoutSuppressed = error.config?.suppressAutoLogout;

        if (
          inappropriateResponseStatuses.includes(error.response?.status) &&
          !isInterceptorSkipRequired
        ) {
          const accessToken = this.#jwtManager.getAccessToken();
          const refreshToken = this.#jwtManager.getRefreshToken();

          const isAccessTokenValid = this.#jwtManager.getIsTokenValid(accessToken);
          const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);

          const isRequestProtected = !!error.config?.headers?.Authorization;

          const isRefreshNeeded =
            !isRefreshRequest &&
            !isAccessTokenValid &&
            isRefreshTokenValid &&
            isRequestProtected &&
            !error.config?.isAuthRetry;

          if (isRefreshNeeded) {
            const config = await this.handleRefresh(error.config);
            _.set(config, 'isAuthRetry', true);

            return httpAccountsApi.request(config);
          }

          if (!isAutoLogoutSuppressed) {
            this.#emitAutoLogoutOnce(REFRESH_TOKEN_EXPIRED_MESSAGE, error);
          }
        }

        return Promise.reject(error);
      },
    );
  };

  #injectRequestInterceptorOfAuthToken = httpApi => {
    httpApi.injectRequestInterceptor(async config => {
      const accessToken = this.#jwtManager.getAccessToken();
      const refreshToken = this.#jwtManager.getRefreshToken();

      const isAccessTokenValid = this.#jwtManager.getIsTokenValid(accessToken);
      const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);
      const isUnprotectedRequest = UNPROTECTED_ROUTES.includes(config.url);

      if (isAccessTokenValid) {
        config.headers.Authorization = `Bearer ${accessToken?.value}`;

        return config;
      }

      if (isUnprotectedRequest) {
        return config;
      }

      if (isRefreshTokenValid) {
        return this.handleRefresh(config);
      }

      this.#emitAutoLogoutOnce(REFRESH_TOKEN_EXPIRED_MESSAGE, generateUnauthorizedAxiosError());

      const abortController = new AbortController();
      abortController.abort();
      config.signal = abortController.signal;

      return config;
    });
  };

  #injectResponseInterceptorOfAuthToken = httpApi => {
    httpApi.injectResponseInterceptor(
      response => response,
      async error => {
        const originalRequest = error.config;

        if (error.response?.status === HTTP_RESPONSE_STATUS_CODES.UNAUTHORIZED) {
          const refreshToken = this.#jwtManager.getRefreshToken();
          const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);

          if (isRefreshTokenValid && !originalRequest?.isAuthRetry) {
            const config = await this.handleRefresh(originalRequest);
            // Without this the replay is indistinguishable from a first attempt, so a request that
            // keeps 401ing on a freshly-minted token refreshes and replays forever.
            _.set(config, 'isAuthRetry', true);

            return httpApi.request(config);
          }

          // A replay rejected WITHOUT the expired-token label is one endpoint's problem, not a dead
          // session — surface it to the caller and leave the session alone. Only a replay the server
          // still calls expired means refreshing is not producing a usable token.
          if (isRefreshTokenValid && !hasExpiredTokenChallenge(error)) {
            return Promise.reject(error);
          }

          this.#jwtManager.setToken(null);
          this.#emitAutoLogoutOnce(REFRESH_TOKEN_EXPIRED_MESSAGE, error);
        }

        return Promise.reject(error);
      },
    );
  };

  #injectResponseInterceptorOfForbiddenStatus = httpApi => {
    httpApi.injectResponseInterceptor(
      response => response,
      async error => {
        const skipForbiddenError = error?.response?.config?.skipResponseInterceptor?.forbiddenError;

        if (
          error.response?.status === HTTP_RESPONSE_STATUS_CODES.FORBIDDEN &&
          !skipForbiddenError
        ) {
          // A 403 raised on a READ while a sub-company (broker) session has the loan editor or the
          // loan pipelines page open is an expected, gracefully-degradable panel denial (subtree-
          // scoped TPO brokers legitimately 403 on secondary reads the deployment doesn't yet
          // authorize them for — e.g. the pipelines page's Kanban prefetch, DEV-5006) — a
          // route-level rule, not a per-endpoint allowlist. Marking the request's own config as
          // suppressed here (rather than only branching locally) means every downstream forbidden-
          // error consumer — catchAsyncActionError's toast dispatch, getDefaultErrorMessage, and
          // the reload branch below — all see it as silent in one place. Employees are unchanged:
          // this only ever fires for a sub-company session. Deliberately scoped to READ methods
          // only (GET/HEAD) — a 403 on a write (POST/PUT/PATCH/DELETE) is a FAILED SAVE, and
          // suppressing it here would silently drop that failure from the broker's view while
          // they believe it persisted. Let non-read 403s fall through to the normal handling
          // below so the toast still fires (DEV-4226).
          if (
            (isLoanEditorRoute() || isPipelinesRoute()) &&
            this.#isSubCompanyUser &&
            isReadRequestError(error)
          ) {
            logger.error('[Auth] Suppressed 403 (sub-company, degradable route, read)', error);
            _.set(error, 'response.config.skipResponseInterceptor.forbiddenError', true);

            return Promise.reject(error);
          }

          // On web a full page reload is a valid last-resort recovery from a stale/forbidden
          // page state — the server re-renders index.html. On a bundled Capacitor app there is
          // NO server to re-fetch from: window.location.reload() just destroys the persistent
          // JS context (Redux + module state + the native SIP registration) and re-runs
          // telephony init every ~5s, opening the transient not-live windows an inbound push
          // falls into (freshConnect → no INVITE → CallKit stuck). A 403 is FORBIDDEN, not
          // UNAUTHORIZED — the 401 path already owns token refresh/logout — so there is no
          // clean generic native recovery for an arbitrary 403; doing nothing (and logging)
          // is strictly better than a reload loop. Native therefore never reloads here (DEV-4332).
          if (getIsNativePlatform()) {
            logger.error('[Auth] Suppressed 403 reload on native platform', error);
          }

          // An employee's 403 on the loan-editor route is still a real problem, but reloading the
          // whole SPA on it turns a gracefully-degradable panel into an infinite reload loop that
          // never lets the editor render. On the loan-editor route a 403 is non-fatal (the toast
          // above still surfaces it for a non-broker session), so suppress the reload here too
          // (the panel's own error handling degrades it) — DEV-4226.
          if (!getIsNativePlatform() && isLoanEditorRoute()) {
            logger.error('[Auth] Suppressed 403 reload on loan-editor route', error);
          }

          // The reload recovery is only valid for a stale/forbidden READ that left the page
          // unusable (e.g. the kanban prefetch GET 403). A WRITE 403 (POST/PUT/PATCH/DELETE) is a
          // FAILED SAVE whose feedback — the caller's error toast + inline message, with the
          // form/wizard still open — must survive; reloading the SPA here wipes all of it, so the
          // Add Loan wizard vanishes with no loan created and no error shown (DEV-5301). Writes
          // fall straight through to the reject below so the caller surfaces the failure.
          if (checkWillReloadOnForbidden(error)) {
            setTimeout(() => window.location.reload(), DEBOUNCE_DELAY_MS);
          }
        }

        return Promise.reject(error);
      },
    );
  };

  // Returns the shared in-flight refresh promise, creating it if none exists. Every concurrent
  // caller (interceptors, handleAuthentication) shares this same promise so only one HTTP round-
  // trip is ever made per "needs-refresh" event (DEV-4332). A failure re-throws so callers can
  // handle the error, and clears the stored tokens only when the session is genuinely unusable.
  #doRefresh = () => {
    if (this.#refreshPromise) {
      return this.#refreshPromise;
    }

    // The teardown is identity-checked: a session boundary can discard this handle while the chain is
    // still in flight, and an unconditional null would then clear a NEWER session's promise — leaving two
    // POST /refresh-token racing on a rotating token, which is the DEV-4332 failure mode.
    const pendingRefresh = this.#refreshAcrossTabs()
      .catch(error => {
        if (this.#isSessionUnrecoverable(error)) {
          this.logout();
        }

        return Promise.reject(error);
      })
      .finally(() => {
        if (this.#refreshPromise === pendingRefresh) {
          this.#refreshPromise = null;
        }
      });

    this.#refreshPromise = pendingRefresh;

    return pendingRefresh;
  };

  // Both session boundaries — ending one (logout) and swapping identity (reLogin) — must disown any refresh
  // still in flight: its result belongs to the old session and must neither persist nor be joined.
  #invalidateInFlightSession = () => {
    this.#sessionEpoch += 1;
    this.#refreshPromise = null;
  };

  // logout() wipes lsAccountsJwtToken, which is SHARED across tabs — so treating every refresh
  // failure as fatal lets one tab's transient failure destroy every tab's healthy session. Two
  // failures reach here that must not do that: a network error (a laptop waking before Wi-Fi
  // associates now triggers a refresh unattended), and losing a cross-tab race, where the rejection
  // means another tab already rotated the token rather than that the session is dead.
  #isSessionUnrecoverable = error => {
    if (this.#jwtManager.getIsTokenValid(this.#jwtManager.getAccessToken())) {
      return false;
    }

    return REFRESH_FATAL_STATUSES.includes(error?.response?.status);
  };

  // #refreshPromise only coalesces refreshes WITHIN a tab. Refresh tokens rotate, so N tabs waking
  // together and each POSTing /refresh-token is the DEV-4332 failure mode: the winner's rotation
  // invalidates the token the losers are still holding. Boot already coordinated through
  // IS_REFRESHING_IN_OTHER_TAB; this extends the same protocol to the mid-session path.
  #refreshAcrossTabs = async () => {
    const isRefreshingElsewhere = this.#storage.getParsedItem(IS_REFRESHING_IN_OTHER_TAB);

    if (isRefreshingElsewhere) {
      await this.handleRefreshInOtherTab();

      if (this.#jwtManager.getIsTokenValid(this.#jwtManager.getAccessToken())) {
        return;
      }
    }

    // A tab closed mid-refresh would otherwise leave the flag set for every survivor.
    const onClearRefreshingFlag = () =>
      this.#storage.setItem(IS_REFRESHING_IN_OTHER_TAB, false, true);

    window.addEventListener('beforeunload', onClearRefreshingFlag);
    this.#storage.setItem(IS_REFRESHING_IN_OTHER_TAB, true, true);

    try {
      await this.refreshToken();
    } finally {
      this.#storage.setItem(IS_REFRESHING_IN_OTHER_TAB, false, true);
      window.removeEventListener('beforeunload', onClearRefreshingFlag);
    }
  };

  handleRefresh = async config => {
    await this.#doRefresh();

    const accessTokenValue = this.#jwtManager.getAccessTokenValue();

    _.set(config, 'headers.Authorization', `Bearer ${accessTokenValue}`);

    return config;
  };

  // Bounded (DEV-6376): a tab that dies between setting the flag and clearing it would otherwise leave
  // every other tab polling forever, turning a lost race into a hung session.
  handleRefreshInOtherTab = async (attemptsLeft = REFRESH_IN_OTHER_TAB_MAX_ATTEMPTS) => {
    const isCurrentlyRefreshing = this.#storage.getParsedItem(IS_REFRESHING_IN_OTHER_TAB);

    if (!isCurrentlyRefreshing || attemptsLeft <= 0) {
      return;
    }

    await delay(RETRY_DELAY_MS);

    return this.handleRefreshInOtherTab(attemptsLeft - 1);
  };

  handleAuthentication = async () => {
    const accessToken = this.#jwtManager.getAccessToken();
    const refreshToken = this.#jwtManager.getRefreshToken();

    const isAccessTokenValid = this.#jwtManager.getIsTokenValid(accessToken);
    const isRefreshTokenValid = this.#jwtManager.getIsTokenValid(refreshToken);

    if (!accessToken && !refreshToken) {
      this.emit('onNoToken');

      return;
    }

    if (isAccessTokenValid) {
      this.#markAuthenticated();
      this.emit('onAutoLogin', true);

      return;
    }

    if (isRefreshTokenValid) {
      try {
        // Need this delay to make sure that tabs will not start processing tokens at the same time
        await delay(0);

        // #doRefresh owns the cross-tab handshake (DEV-6376), so boot and mid-session share one
        // protocol, and any interceptor-triggered handleRefresh firing concurrently (e.g. a background
        // push-wake call during the SplashScreen phase) joins the same promise instead of issuing a
        // second POST /refresh.
        await this.#doRefresh();

        this.#markAuthenticated();
        this.emit('onAutoLogin', true);

        return;
      } catch {
        this.#endDeadSessionAtBoot();

        return;
      }
    }

    this.#endDeadSessionAtBoot();
  };

  // handleAuthentication only ever runs at BOOT, so a stored session that is already dead means the
  // user is simply not signed in — the same state as having no token at all, which emits onNoToken
  // silently. Raising "RefreshToken is expired" at someone who has not logged in is noise they can
  // neither act on nor understand, and on iOS it is near-permanent: keychain items OUTLIVE app
  // deletion, so one stale token haunts every cold launch of a reinstalled app. Mid-session expiry
  // is a different story and still surfaces — that runs through the axios response interceptors.
  #endDeadSessionAtBoot = () => {
    this.#jwtManager.setToken(null);
    this.emit('onNoToken');
  };

  getInitialCompanyId = () =>
    authApi
      .getTenant({ domainName: getDomainName() })
      .then(({ data }) => {
        this.setCompanyId(data.id);
        this.emit('onTenantLoaded', {
          domainType: data.domainType ?? null,
          name: data.name ?? null,
          branding: data.branding ?? null,
        });
      })
      .catch(error => Promise.reject(error));

  // The epoch is captured BEFORE the round trip: a refresh that resolves after the session it belongs to
  // ended must not write its tokens back. Otherwise an unattended refresh — which the proactive timer and
  // the focus/visibilitychange listeners now make possible at any moment, including the instant before the
  // user clicks Log out — resurrects a valid access + refresh pair in shared localStorage after the wipe,
  // and the next boot silently auto-logs that identity back in.
  refreshToken = () => {
    const sessionEpoch = this.#sessionEpoch;
    const clientId = this.#storage.getItem(LS_ACCOUNTS_CLIENT_ID);

    return authApi
      .refreshToken(
        { refreshTokenValue: this.#jwtManager.getRefreshTokenValue() },
        getConditionalObject(!!clientId, () => ({ clientId })),
      )
      .then(({ data }) => {
        // Rejecting rather than resolving null: a silent success would let handleRefresh stamp
        // `Bearer undefined` from the now-wiped storage and replay the request, which 401s and raises a
        // "RefreshToken is expired" auto-logout at a user who just clicked Log out.
        if (sessionEpoch !== this.#sessionEpoch) {
          return Promise.reject(new Error(REFRESH_ABANDONED_MESSAGE));
        }

        return this.#jwtManager.setToken(data);
      })
      .catch(error => Promise.reject(error));
  };

  trustDevice = () =>
    authApi
      .trustDevice()
      .then(({ data }) => this.#jwtManager.setToken(data))
      .catch(error => Promise.reject(error));

  signInWithEmailAndPassword = ({ email, password }) =>
    authApi
      .signInWithCredentials({
        email,
        password,
        companyId: getCompanyId(),
        domain: getDomainName(),
      })
      .then(({ data }) => {
        this.#jwtManager.setToken(data);

        return this.signInWithToken();
      })
      .catch(error => Promise.reject(error));

  // DEV-3727: when the auto-login carries a refresh token, store it next to the access token so the
  // host session refreshes on access-token expiry instead of emitting the misleading "RefreshToken is
  // expired" auto-logout. The refresh token is opaque (not a JWT), so its expiry is supplied explicitly
  // rather than decoded.
  // DEV-3807: persistence is split out from signInWithAccessToken so the handoff frame can seed the
  // token into storage synchronously (before any authenticated request fires) without also making the
  // authorization-context network call. Returns whether the token was persisted.
  persistAccessToken = (token, refreshToken = null, refreshTokenExpiresAt = null) => {
    const decodedToken = jwtDecode(token);

    if (!decodedToken?.exp) {
      return false;
    }

    const expirationDate = dayjs.unix(decodedToken.exp).toISOString();
    const hasRefreshToken = !_.isNil(refreshToken) && !_.isNil(refreshTokenExpiresAt);

    this.#jwtManager.setToken({
      accessValue: { value: token, expirationDate },
      ...getConditionalObject(hasRefreshToken, () => ({
        refreshValue: { value: refreshToken, expirationDate: refreshTokenExpiresAt },
      })),
    });

    return true;
  };

  signInWithAccessToken = (token, refreshToken = null, refreshTokenExpiresAt = null) => {
    try {
      const isPersisted = this.persistAccessToken(token, refreshToken, refreshTokenExpiresAt);

      if (!isPersisted) {
        return Promise.reject(new Error('Access token is missing exp claim'));
      }
    } catch (error) {
      return Promise.reject(error);
    }

    return this.signInWithToken({ suppressAutoLogout: true });
  };

  signInWithToken = config => {
    const clientId = this.#storage.getItem(LS_ACCOUNTS_CLIENT_ID);

    return authApi
      .signInWithAuthContext(
        config,
        getConditionalObject(!!clientId, () => ({ clientId })),
      )
      .then(({ data, status }) => {
        const isSuccessResponse = status === HTTP_RESPONSE_STATUS_CODES.SUCCESS;

        if (!isSuccessResponse) {
          this.logout();

          return Promise.reject({ response: { data: 'Failed to login with token.' } });
        }

        this.#isLoggedOut = false;
        this.#markAuthenticated();

        const {
          isImpersonationApplied,
          user: { id: userId, firstName, lastName, email, fullName, webTheme },
          company: { id: companyId, type: companyTypeData, ...companyData },
          roles,
          subCompany,
        } = data;

        this.#isSubCompanyUser = !!subCompany;

        aiAggregator?.setUserContext({
          authenticatedUserId: `${email}-${companyId}`,
          storeInCookie: true,
        });

        const permissions = roles.reduce((acc, role) => [...acc, ...role.claims], []);

        this.setInterceptors();

        return {
          uuid: userId,
          companyId,
          subCompany,
          company: {
            companyId,
            type: companyTypeData?.type,
            ...companyData,
          },
          role: roles.filter(({ isDeleted }) => !isDeleted).map(r => r.name),
          permissions,
          data: {
            firstName,
            lastName,
            email,
            webTheme,
            impersonateMode: isImpersonationApplied,
            displayName: fullName,
            avatarDefault: firstName[0] + lastName[0],
            photoURL: '',
            settings: {
              ...masterSettings,
              themeMode:
                (IS_SWITCH_THEME_MODE_ALLOWED &&
                  themeOptions.find(({ value }) => value === webTheme)?.value) ||
                DEFAULT_THEME_MODE,
            },
          },
        };
      })
      .catch(error => {
        this.logout();

        if (error.isAxiosError) {
          return Promise.reject(error?.response?.data);
        }

        return Promise.reject(error);
      });
  };

  // A guest-portal client (e.g. Closing Portal) signs in with a one-time email code rather than
  // the AdminPanel credentials flow. The resolved clientId is persisted so every subsequent
  // refreshToken/signInWithToken call keeps authenticating against the same client registration.
  signInWithOtpCode = ({ requestId, code, clientId }) =>
    authApi
      .signInWithOtpRequest({ requestId, code, clientId })
      .then(({ data }) => {
        this.#jwtManager.setToken(data);
        this.#storage.setItem(LS_ACCOUNTS_CLIENT_ID, clientId);

        return this.signInWithToken();
      })
      .catch(error => Promise.reject(error));

  setCompanyId = id => {
    if (id) {
      this.#storage.setItem(LS_ACCOUNTS_COMPANY_ID, id);

      return;
    }

    this.#storage.removeItem(LS_ACCOUNTS_COMPANY_ID);
  };

  // A session that ends mid-impersonation (expiry, or an explicit logout) never goes through
  // revertImpersonate, so the company context it switched would outlive the session. The login form
  // signs in against getCompanyId(), and the operator's credentials are rejected by the impersonated
  // company — reported to the user as "email or password incorrect" until a reload re-derives it.
  #restoreOperatorCompany = () => {
    const operatorCompanyId = this.#storage.getItem(IMPERSONATED_FROM_COMPANY_ID);

    if (operatorCompanyId) {
      this.setCompanyId(operatorCompanyId);
    }

    this.#storage.removeItem(IMPERSONATED_FROM_COMPANY_ID);
    this.#storage.removeItem(IMPERSONATED_FROM_PATH_NAME);
  };

  logout = ({ isExplicitLogout = false } = {}) => {
    this.#invalidateInFlightSession();
    this.#teardownRefreshCycle();
    this.#jwtManager.setToken(null);
    this.#restoreOperatorCompany();
    this.#isSubCompanyUser = false;
    aiAggregator?.clearUserContext();
    shutdownIntercomMessenger();

    const resetLSKeys = isExplicitLogout
      ? LS_KEYS_TO_RESET_ON_LOGOUT_EXPLICIT
      : LS_KEYS_TO_RESET_ON_LOGOUT;

    resetLSKeys.forEach(key => this.#storage.removeItem(key));
  };

  reLogin = (tokenData, redirectionPath = '/') => {
    // On native the in-place history.push below keeps the JS context alive across the identity
    // swap — a booted messenger would keep the PREVIOUS user's Intercom session (impersonation
    // enters and exits through here without logout()).
    shutdownIntercomMessenger();
    // The identity is changing, so a refresh still in flight for the previous one must not clobber the
    // token being stored here.
    this.#invalidateInFlightSession();
    this.#isLoggedOut = false;
    this.#jwtManager.setToken(tokenData);
    // Re-armed against the NEW identity's token. On web the navigation below reboots the app and
    // handleAuthentication would arm it anyway, but the native branch is an in-place history.push whose
    // JS context survives — without this, an impersonated native session keeps the previous identity's
    // schedule and then loses it entirely.
    this.#startRefreshCycle();

    // Web: a hard navigation re-boots the app so Authentication re-runs init() and picks up the
    // newly-stored (impersonation) token. Native: a hard navigation to capacitor://localhost is a
    // full document reload that tears down the persistent JS context + native SIP registration
    // (the DEV-4332 churn). Use React Router (history.push) instead so the token swap stays in-
    // place without destroying the WebView runtime.
    if (getIsNativePlatform()) {
      history.push(redirectionPath);

      return;
    }

    window.location.href = redirectionPath;
  };

  impersonate = ({ targetUserId, targetCompanyId }) =>
    authApi
      .signInWithImpersonationRequest(
        { targetUserId, targetCompanyId, domain: getDomainName() },
        { headers: { companyId: getCompanyId() } },
      )
      .then(({ data }) =>
        authApi.signInWithImpersonate(
          { requestId: data.id, domain: getDomainName() },
          {
            headers: {
              code: data.type.confirmationCode,
              companyId: getCompanyId(),
            },
          },
        ),
      )
      .then(({ data }) => {
        this.#storage.setItem(IMPERSONATED_FROM_PATH_NAME, window.location.pathname);
        // The CompanyId header on company-scoped requests is derived from lsAccountsCompanyId, not
        // the JWT — so impersonating a user in another company must switch the company context too,
        // else those requests keep hitting the operator's company (DEV-5153). Stash the operator's
        // company to restore on revert. Guard the write: storage.setItem coerces a null to the
        // string "null", which would later read back truthy and get persisted as a bogus company id.
        const operatorCompanyId = getCompanyId();

        if (operatorCompanyId) {
          this.#storage.setItem(IMPERSONATED_FROM_COMPANY_ID, operatorCompanyId);
        }

        this.setCompanyId(targetCompanyId);
        this.reLogin(data);
      })
      .catch(error => Promise.reject(error));

  revertImpersonate = () =>
    authApi
      .signInRevertImpersonation(null, {
        headers: { companyId: getCompanyId(), domain: getDomainName() },
      })
      .then(({ data }) => {
        const redirectionPath = this.#storage.getItem(IMPERSONATED_FROM_PATH_NAME);
        // Restore the operator's stashed company context. The stash is only ever a real company id
        // or absent (null) — never the literal "null" (see the guarded write in impersonate). In the
        // normal impersonate->revert flow it is always present. If it is absent, setCompanyId(null)
        // clears lsAccountsCompanyId; on web the reLogin hard reload then re-derives it from the
        // domain tenant via init(). (Native reLogin is a soft history.push with no reload, but the
        // absent-stash branch is unreachable there since impersonate always stashes a real company.)
        this.setCompanyId(this.#storage.getItem(IMPERSONATED_FROM_COMPANY_ID));
        this.reLogin(data, redirectionPath || undefined);
        this.#storage.removeItem(IMPERSONATED_FROM_PATH_NAME);
        this.#storage.removeItem(IMPERSONATED_FROM_COMPANY_ID);
      })
      .catch(error => Promise.reject(error));
}

export default AccountsJwtService;
